import type { ElementRef } from 'react'
import { forwardRef } from 'react'
import type { SwitchProps as AriaSwitchProps } from 'react-aria-components'
import { Switch as AriaSwitch, composeRenderProps } from 'react-aria-components'
import { tv } from 'tailwind-variants'

export type UISwitchProps = {
  labelPlacement?: 'start' | 'end'
  size?: 'normal' | 'small' | 'xsmall'
  /**
   * @deprecated use `isSelected` instead
   */
  checked?: boolean
  /**
   * @deprecated use `onChange` instead
   * @param isSelected
   * @returns
   */
  onCheckedChange?: (isSelected: boolean) => void

  /** Alias of `isDisabled` */
  disabled?: AriaSwitchProps['isDisabled']
  children?: AriaSwitchProps['children']

  SwitchProps?: Omit<
    AriaSwitchProps,
    | 'className'
    | 'children'
    | 'style'
    | 'isDisabled'
    | 'onChange'
    | 'isSelected'
  >
} & Pick<AriaSwitchProps, 'isSelected' | 'onChange'> &
  StyleProps

export const switchRootStyles = tv({
  base: 'rounded-full transition flex gap-2',
  variants: {
    labelPlacement: {
      start: 'flex-row-reverse',
      end: '',
    },
    isDisabled: {
      true: 'opacity-50',
    },
  },
})

export const switchTrackStyles = tv({
  base: 'bg-neutral-200 dark:bg-neutral-600 flex shrink-0 items-center rounded-full',
  // FIXME: type placeholder
  variants: {
    size: {
      normal: 'w-10 h-6 p-[3px]',
      small: 'w-6 h-4 p-[2px]',
      xsmall: 'w-[22px] h-3 p-px',
    },
    isSelected: { true: 'dark:bg-neutral-50 bg-black' },
  },
})

export const switchHandleStyles = tv({
  base: 'block bg-neutral-50 dark:bg-neutral-800 rounded-full shadow transition',
  variants: {
    size: {
      normal: 'size-[18px]',
      small: 'size-3',
      xsmall: 'size-[10px]',
    },
  },
  compoundVariants: [
    {
      size: 'normal',
      isSelected: true,
      class: 'translate-x-[15px]',
    },
    {
      size: 'small',
      isSelected: true,
      class: 'translate-x-2',
    },
    {
      size: 'xsmall',
      isSelected: true,
      class: 'translate-x-[10px]',
    },
  ],
})

const UISwitch = forwardRef<ElementRef<'label'>, UISwitchProps>(function (
  {
    className,
    style,
    size = 'normal',
    children,
    labelPlacement,
    checked,
    onCheckedChange,
    SwitchProps,
    onChange,
    isSelected,
    disabled,
  },
  ref,
) {
  return (
    <AriaSwitch
      ref={ref}
      className={composeRenderProps(className, (className, renderProps) =>
        switchRootStyles({ ...renderProps, className, labelPlacement }),
      )}
      style={style}
      // Compatibility with radix-ui
      isSelected={isSelected ?? checked}
      isDisabled={disabled}
      onChange={onChange ?? onCheckedChange}
      {...SwitchProps}
    >
      {composeRenderProps(
        children as AriaSwitchProps['children'],
        (children, ariaRenderProps) => (
          <>
            <div className={switchTrackStyles({ ...ariaRenderProps, size })}>
              <span
                className={switchHandleStyles({ ...ariaRenderProps, size })}
              />
            </div>
            {children}
          </>
        ),
      )}
    </AriaSwitch>
  )
})
UISwitch.displayName = 'UISwitch'

export default UISwitch
